import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { endOfMonth } from 'date-fns';
import { RequisitionService } from '../../../core/requisition.service';
import { Priority } from '../../models/priority.model';
import { ProjectsList } from '../../models/projects.model';
import {
  Requisition,
  RequisitionColumn,
  RequisitionColumnEnum,
  RequisitionFilters,
  SelectableRequisition,
  UsersWho,
} from '../../models/requisition.model';
import { Status } from '../../models/status.model';
import { User } from '../../models/user.model';

@Component({
  selector: 'app-requisition-table',
  templateUrl: './requisition-table.component.html',
  styleUrl: './requisition-table.component.scss',
})
export class RequisitionTableComponent implements OnInit {
  @Input() project?: ProjectsList | 'all';

  private _projectId: number | 'all' = 0;
  @Input() set projectId(value: number | 'all') {
    this._projectId = value;
  }
  get projectId(): number | 'all' {
    return this._projectId;
  }

  get projectName(): string | 'all' {
    return this.project !== 'all'
      ? this.project?.projectName || ''
      : 'Todos los proyectos' + (this.selectedApplicationDates.dates?.length === 0 ? ' - últimos 30 días' : '');
  }

  @Input() requisitionTabsList: string[] = [];
  @Input() extraActionButtonText: string = 'Ir a mis requisiciones';
  @Input() requisitionTableData: Requisition[] = [];
  @Input() showAuthorizeColumn: boolean = false;
  @Input() totalRequisitions: number = 0;
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 0;
  @Input() loading: boolean = false;
  @Input() priorityList: Priority[] = [] as Priority[];
  @Input() statusList: Status[] = [] as Status[];
  @Input() progressList: Status[] = [] as Status[];
  @Input() appliedFilters!: RequisitionFilters;
  @Input() order: string = '';
  @Input() usersInRequisitions: UsersWho = {
    userWhoAuthorizes: [] = [],
    userWhoBuys: [] = [],
    userWhoRequests: [] = [],
    userWhoReviews: [] = [],
  };
  @Input() showUserWhoAuthorizesColumn = false;
  @Input() showUserWhoBuysColumn = false;
  @Input() showUserWhoRequestsColumn = true;
  @Input() showUserWhoReviewsColumn = false;
  @Input() selectedRequisitions: Set<number> = new Set();
  @Input() canPrint = false;
  @Input() userPermissions?: {
    canViewAmounts?: boolean;
    isAdmin?: boolean;
  };

  @Output() changeRequisitionTab = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() descriptionTextFilter = new EventEmitter<string>();
  @Output() projectFilter = new EventEmitter<string>();
  @Output() sortColumn = new EventEmitter<{
    columnName: string;
    direction: string;
  }>();
  @Output() priorityFilter = new EventEmitter<Priority[]>();
  @Output() statusFilter = new EventEmitter<Status[]>();
  @Output() progressFilter = new EventEmitter<Status[]>();
  @Output() applicationDateFilter = new EventEmitter<{
    dates?: Date[];
    columnName?: string;
  }>();
  @Output() expectedDeliveryDateFilter = new EventEmitter<{
    dates?: Date[];
    columnName?: string;
  }>();
  @Output() highlightRequisition = new EventEmitter<{
    requisition: Requisition;
    tableIndex: number;
  }>();
  @Output() deleteRequisition = new EventEmitter<number>();
  @Output() goToRequisitionDetails = new EventEmitter<Requisition>();
  @Output() userWhoBuysFilter = new EventEmitter<{
    user: User[];
    columnName: string;
  }>();
  @Output() userWhoAuthorizesFilter = new EventEmitter<{
    user: User[];
    columnName: string;
  }>();
  @Output() userWhoReviewsFilter = new EventEmitter<{
    user: User[];
    columnName: string;
  }>();
  @Output() userWhoRequestsFilter = new EventEmitter<{
    user: User[];
    columnName: string;
  }>();
  @Output() idTextFilter = new EventEmitter<string>();
  @Output() folioTextFilter = new EventEmitter<string>();
  @Output() clearAllFilters = new EventEmitter<void>();
  @Output() checkedRequisitions = new EventEmitter<{id: number, checked: boolean}[]>();
  @Output() exportRequisitions = new EventEmitter<'selected' | 'all'>();

  @ViewChild('folioInput') folioInput?: ElementRef;
  @ViewChild('descriptionInput') descriptionInput?: ElementRef;
  @ViewChild('projectInput') projectInput?: ElementRef;

  public requisitionTableDataBackup: Requisition[] = [];
  public currentTab = 0;
  public areAllRequisitionsSelected: boolean = false;
  public currentPageRequisitions: SelectableRequisition[] = [];
  public selectedPriority: Priority[] = [] as Priority[];
  public selectedStatus: Status[] = [] as Status[];
  public selectedProgress: Status[] = [] as Status[];
  public selectedApplicationDates: { dates?: Date[]; columnName?: string } = {};
  public selectedExpectedDeliveryDates: {
    dates?: Date[];
    columnName?: string;
  } = {};
  public selectedIdFilter: string = '';
  public selectedFolioFilter: string = '';
  public selectedDescriptionFilter: string = '';
  public selectedProjectFilter: string = '';
  public isSelectedIdFilter: boolean = false;
  public isSelectedFolioFilter: boolean = false;
  public isSelectedDescriptionFilter: boolean = false;
  public isSelectedProjectFilter: boolean = false;
  public requisitionCheckableColumnsList: RequisitionColumn[] = [];
  public openedNotes = new Set<number>();
  public selectedSortColumn: string = '';
  public selectedSortDirection: string = '';
  public isSortActive = false;
  public selectedUserWhoBuys: User[] = [];
  public selectedUserWhoAuthorizes: User[] = [];
  public selectedUserWhoReviews: User[] = [];
  public selectedUserWhoRequests: User[] = [];
  public paginatorItems: number[] = [];
  public itemsPerPage: number[] = [10, 20, 30, 50];
  public areFiltersEmpty = true;

  public requisitionColumnsEnum = RequisitionColumnEnum;

  public requisitionColumnsList: RequisitionColumn[] = [
    {
      title: 'Solicita',
      name: 'userWhoRequests',
      hasShowAndHideColumnFilter: true,
      isVisible: this.showUserWhoRequestsColumn,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'ID',
      name: 'requisitionKey',
      hasShowAndHideColumnFilter: true,
      isVisible: false,
      hasSearchFilter: false,
      isSortable: true,
      width: '200px',
      align: 'left',
    },
    {
      title: 'Folio',
      name: 'requisitionFolio',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSearchFilter: true,
      isSortable: true,
      width: '120px',
      align: 'left',
    },
    {
      title: 'Descripción',
      name: 'requisitionDescription',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'Proyecto',
      name: 'requisitionProject',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'Fecha de solicitud',
      name: 'requisitionApplicationDate',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasDateFilter: true,
      isSortable: true,
      width: '230px',
      align: 'center',
    },
    {
      title: 'Entrega esperada',
      name: 'requisitionExpectedDeliveryDate',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasDateFilter: true,
      isSortable: true,
      width: '230px',
      align: 'center',
    },
    {
      title: 'Monto requisitado',
      name: 'requisitionTotalCost',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      isSortable: true,
      width: '200px',
      align: 'center',
    },
    {
      title: 'Compras',
      name: 'userWhoBuys',
      hasShowAndHideColumnFilter: true,
      isVisible: this.showUserWhoBuysColumn,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'Autoriza',
      name: 'userWhoAuthorizes',
      hasShowAndHideColumnFilter: true,
      isVisible: this.showUserWhoAuthorizesColumn,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'Revisa',
      name: 'userWhoReviews',
      hasShowAndHideColumnFilter: true,
      isVisible: this.showUserWhoReviewsColumn,
      hasSearchFilter: true,
      isSortable: true,
      width: '350px',
      align: 'left',
    },
    {
      title: 'Prioridad',
      name: 'requisitionPriority',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSelectableFilter: true,
      isSortable: true,
      width: '140px',
      align: 'center',
    },
    {
      title: 'Estado de requisición',
      name: 'requisitionStatus',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSelectableFilter: true,
      isSortable: true,
      width: '220px',
      align: 'center',
    },
    {
      title: 'Progreso actual',
      name: 'requisitionProgress',
      hasShowAndHideColumnFilter: true,
      isVisible: true,
      hasSelectableFilter: true,
      isSortable: true,
      width: '200px',
      align: 'center',
    },
    {
      title: '',
      name: 'Actions',
      hasShowAndHideColumnFilter: false,
      isVisible: true,
      width: '50px',
      align: 'center',
    }
  ];
  public ranges = {
    Ayer: [
      new Date(new Date().setDate(new Date().getDate() - 1)),
      new Date(new Date().setDate(new Date().getDate() - 1)),
    ],
    Hoy: [new Date(), new Date()],
    Mañana: [
      new Date(new Date().setDate(new Date().getDate() + 1)),
      new Date(new Date().setDate(new Date().getDate() + 1)),
    ],
    'Últimos 7 días': [
      new Date(new Date().setDate(new Date().getDate() - 7)),
      new Date(),
    ],
    'Semana pasada': [
      new Date(
        new Date().setDate(new Date().getDate() - new Date().getDay() - 7)
      ),
      new Date(
        new Date().setDate(new Date().getDate() - new Date().getDay() - 1)
      ),
    ],
    'Esta semana': [
      new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
      new Date(
        new Date().setDate(new Date().getDate() + (6 - new Date().getDay()))
      ),
    ],
    'Próxima semana': [
      new Date(
        new Date().setDate(new Date().getDate() + (7 - new Date().getDay()))
      ),
      new Date(
        new Date().setDate(new Date().getDate() + (13 - new Date().getDay()))
      ),
    ],
    'Próximos 7 días': [
      new Date(),
      new Date(new Date().setDate(new Date().getDate() + 7)),
    ],
    'Últimos 30 días': [
      new Date(new Date().setDate(new Date().getDate() - 30)),
      new Date(),
    ],
    'Mes pasado': [
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() - 1)),
    ],
    'Este mes': [
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endOfMonth(new Date()),
    ],
    'Próximo mes': [
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1),
      endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 1)),
    ],
    'Próximos 30 días': [
      new Date(),
      new Date(new Date().setDate(new Date().getDate() + 30)),
    ],
  };

  constructor(private router: Router, private clipboard: Clipboard,
              private requisitionService: RequisitionService) {
    if (this.projectId === "all") {
      this.project = 'all'
    }
  }

  ngOnInit(): void {
    if (this.currentPage === 0) this.currentPage = 1;
  }

  ngOnChanges(): void {
    this.paginatorItems =
      this.totalRequisitions / this.pageSize
        ? [
            ...Array(Math.ceil(this.totalRequisitions / this.pageSize)).keys(),
          ].map((x) => x + 1)
        : [];
    this.requisitionTableDataBackup = [...this.requisitionTableData];
    this.requisitionCheckableColumnsList = [...this.requisitionColumnsList];
    this.currentPageRequisitions = [
      ...this.requisitionTableData.map((y, i) => ({
        requisitionId: y.requisitionId,
        requisitionDescription: y.requisitionDescription,
        isSelected: false,
        isIdTooltipVisible: false,
        isFolioTooltipVisible: false,
      })),
    ];
    this.validateSelectedRequisitions();
    this.convertParamsToAppliedFilters(this.appliedFilters);
    this.convertParamsToSort(this.order);
    this.requisitionColumnsList[this.requisitionColumnsEnum.TotalCost].isVisible = this.userPermissions?.canViewAmounts ?? false;

  }

  onExportRequisitions(value: 'all' | 'selected') {
    if (value === 'selected' && this.selectedRequisitions.size === 0) return
    this.exportRequisitions.emit(value);
  }

  onChangeRequisitionTab(tab: number) {
    this.currentTab = tab;
    this.changeRequisitionTab.emit(tab);
  }

  goToRequisitions() {
    this.router.navigate(['/requisiciones/all']);
  }

  onFilterColumnsList(e: any) {
    if (e.target.value.length == '') {
      this.requisitionCheckableColumnsList = [...this.requisitionColumnsList];
      return;
    }
    this.requisitionCheckableColumnsList = this.requisitionColumnsList.filter((y) =>
                                            y.title.toLowerCase()
                                            .includes(e.target.value.toLowerCase()));
  }

  onSelectRequisition(index: number, e: boolean) {
    const currentSelected =
      this.currentPageRequisitions.filter((x) => x.isSelected);
    const areAllSelected =
      this.currentPageRequisitions.length === currentSelected.length;
    if (e) {
      this.currentPageRequisitions[index].isSelected = true;
      if (areAllSelected) this.areAllRequisitionsSelected = true;
    } else {
      this.currentPageRequisitions[index].isSelected = false;
      if (!areAllSelected) this.areAllRequisitionsSelected = false;
    }
    this.checkedRequisitions.emit([{id: this.currentPageRequisitions[index].requisitionId, checked: e}]);
  }

  onSelectAllRequisitions() {
    if (this.areAllRequisitionsSelected) {
      this.currentPageRequisitions.forEach((y) => (y.isSelected = true));
    } else {
      this.currentPageRequisitions.forEach((y) => (y.isSelected = false));
    }
    this.checkedRequisitions.emit(
      this.currentPageRequisitions.map((x) => { return {id: x.requisitionId, checked: x.isSelected}})
    );
  }

  onSortColumn(columnName: string) {
    if (!columnName) return;
    if (columnName === this.selectedSortColumn) {
      if (this.selectedSortDirection === 'desc') {
        this.selectedSortColumn = '';
        this.selectedSortDirection = '';
        this.isSortActive = false;
      } else {
        this.selectedSortDirection = 'desc';
      }
    } else {
      this.isSortActive = true;
      this.selectedSortColumn = columnName;
      this.selectedSortDirection = 'asc';
    }
    this.sortColumn.emit({
      columnName: columnName,
      direction: this.selectedSortDirection,
    });
  }

  onShowFolioFilter() {
    setTimeout(() => {
      this.folioInput?.nativeElement.focus();
    }, 200);
  }

  onShowDescriptionFilter() {
    setTimeout(() => {
      this.descriptionInput?.nativeElement.focus();
    }, 200);
  }

  onShowProjectFilter() {
    setTimeout(() => {
      this.projectInput?.nativeElement.focus();
    }, 200);
  }

  onIdFilterChange(value: string) {
    this.idTextFilter.emit(value);
    this.isSelectedIdFilter = false;
    this.currentPage = 1;
  }

  onResetIdFilter() {
    this.idTextFilter.emit('');
    this.selectedIdFilter = '';
    this.isSelectedIdFilter = false;
    this.currentPage = 1;
  }

  onFolioFilterChange(value: string) {
    this.folioTextFilter.emit(value);
    this.isSelectedFolioFilter = false;
    this.currentPage = 1;
  }

  onResetFolioFilter() {
    this.folioTextFilter.emit('');
    this.isSelectedFolioFilter = false;
    this.currentPage = 1;
  }

  onDescriptionFilterChange(value: string) {
    this.descriptionTextFilter.emit(value);
    this.isSelectedDescriptionFilter = false;
    this.currentPage = 1;
  }

  onResetDescriptionFilter() {
    this.descriptionTextFilter.emit('');
    this.isSelectedDescriptionFilter = false;
    this.currentPage = 1;
  }

  onProjectFilterChange(value: string) {
    this.projectFilter.emit(value);
    this.isSelectedProjectFilter = false;
    this.currentPage = 1;
  }

  onResetProjectFilter() {
    this.projectFilter.emit('');
    this.isSelectedProjectFilter = false;
    this.currentPage = 1;
  }

  onDateFilterChange(
    rangeSelected: Date[],
    column: string,
    isAdding = true
  ): void {
    rangeSelected = rangeSelected.map((x) => new Date(x.setHours(23, 59, 59, 999)));
    if (isAdding) {
      if (column === 'requisitionApplicationDate') {
        this.selectedApplicationDates = {
          dates: rangeSelected,
          columnName: column,
        };
        this.applicationDateFilter.emit(this.selectedApplicationDates);
      } else if (column === 'requisitionExpectedDeliveryDate') {
        this.selectedExpectedDeliveryDates = {
          dates: rangeSelected,
          columnName: column,
        };
        this.expectedDeliveryDateFilter.emit(
          this.selectedExpectedDeliveryDates
        );
      }
    } else {
      if (column === 'requisitionApplicationDate') {
        this.selectedApplicationDates = {};
        this.applicationDateFilter.emit(this.selectedApplicationDates);
      } else if (column === 'requisitionExpectedDeliveryDate') {
        this.selectedExpectedDeliveryDates = {};
        this.expectedDeliveryDateFilter.emit(
          this.selectedExpectedDeliveryDates
        );
      }
    }
    this.currentPage = 1;
  }

  onUserFilter(e: any, columnName: string) {
    this.userWhoBuysFilter.emit({ user: e, columnName: columnName });
    this.currentPage = 1;
  }

  onPriorityFilterChange(priority: Priority, isAdding = true) {
    if (
      isAdding &&
      this.selectedPriority.findIndex((x) => x.id === priority.id) === -1
    ) {
      this.selectedPriority.push(priority);
    } else {
      this.selectedPriority = this.selectedPriority.filter(
        (y) => y.id !== priority.id
      );
    }
    this.priorityFilter.emit(this.selectedPriority);
    this.currentPage = 1;
  }

  onStatusFilterChange(status: Status, isAdding = true) {
    if (
      isAdding &&
      this.selectedStatus.findIndex((x) => x.id === status.id) === -1
    ) {
      this.selectedStatus.push(status);
    } else {
      this.selectedStatus = this.selectedStatus.filter(
        (y) => y.id !== status.id
      );
    }
    this.statusFilter.emit(this.selectedStatus);
    this.currentPage = 1;
  }

  onProgressFilterChange(progress: Status, isAdding = true) {
    if (
      isAdding &&
      this.selectedProgress.findIndex((x) => x.id === progress.id) === -1
    ) {
      this.selectedProgress.push(progress);
    } else {
      this.selectedProgress = this.selectedProgress.filter(
        (y) => y.id !== progress.id
      );
    }
    this.progressFilter.emit(this.selectedProgress);
    this.currentPage = 1;
  }

  onClearAllFilters() {
    if (!this.appliedFilters) return;
    this.selectedPriority = [];
    this.selectedStatus = [];
    this.selectedProgress = [];
    this.selectedApplicationDates = {};
    this.selectedExpectedDeliveryDates = {};
    this.selectedProjectFilter = '';
    this.selectedDescriptionFilter = '';
    this.selectedSortColumn = '';
    this.selectedSortDirection = '';
    this.isSortActive = false;
    this.selectedUserWhoBuys = [];
    this.selectedUserWhoAuthorizes = [];
    this.selectedUserWhoReviews = [];
    this.selectedUserWhoRequests = [];
    this.clearAllFilters.emit();
    this.currentPage = 1;
  }

  onCopyToClipboard(value: string, index: number, $event: any, isIdColumn: boolean = false) {
    $event.stopPropagation();
    $event.preventDefault();
    if (value === '') return
    this.clipboard.copy(value);
    isIdColumn ? this.currentPageRequisitions[index].isIdTooltipVisible = true
      : this.currentPageRequisitions[index].isFolioTooltipVisible = true;

    setTimeout(() => {
      isIdColumn ? this.currentPageRequisitions[index].isIdTooltipVisible = false
        : this.currentPageRequisitions[index].isFolioTooltipVisible = false;
    }, 1000);
  }

  onGoToRequisitionDetails(requisition: Requisition, $event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    this.goToRequisitionDetails.emit(requisition);
  }

  onHighlightRequisition(
    data: Requisition,
    isHighlighted: boolean,
    tableIndex: number,
    $event: any
  ) {
    $event.stopPropagation();
    $event.preventDefault();
    this.highlightRequisition.emit({
      requisition: { ...data, requisitionIsHighlighted: isHighlighted },
      tableIndex: tableIndex,
    });
  }

  onDeleteRequisition(requisitionId: number, $event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    this.deleteRequisition.emit(requisitionId);
  }

  onSelectNote(id: number, $event: any) {
    $event.stopPropagation();
    $event.preventDefault();
    this.openedNotes.has(id) ? this.openedNotes.delete(id) : this.openedNotes.add(id);
  }

  onCloseNote(id: number) {
    this.openedNotes.delete(id);
  }

  onPageIndexChange(newPage: number) {
    this.currentPage = newPage;
    this.pageChange.emit(this.currentPage);
  }

  onPageSizeChange(newPageSize: number) {
    this.pageSize = newPageSize;
    this.pageSizeChange.emit(this.pageSize);
  }

  convertParamsToAppliedFilters(params: RequisitionFilters): void {
    if (params === undefined) return;
    this.areFiltersEmpty = this.validateFiltersEmptiness();
    this.selectedPriority =
      this.priorityList.filter((x) =>
        params.priorities.includes(x.id.toString())
      );
    this.selectedStatus = this.statusList
                            .filter((x) => params.states.includes(x.id.toString()));
    this.selectedProgress = this.progressList
                            .filter((x) => params.progress.includes(x.id.toString()));
    this.currentTab = this.selectedProgress.length === 1 ? this.selectedProgress[0].id + 1 : 0;
    this.selectedApplicationDates = params.applicationDate
      ? {
          dates: [...params.applicationDate.map((x) => new Date(x))],
          columnName: 'requisitionApplicationDate',
        }
      : this.selectedApplicationDates;
    this.selectedExpectedDeliveryDates = params.expectedDeliveryDate
      ? {
          dates: [...params.expectedDeliveryDate.map((x) => new Date(x))],
          columnName: 'requisitionExpectedDeliveryDate',
        }
      : this.selectedExpectedDeliveryDates;
    this.selectedIdFilter = params.idFilter
      ? params.idFilter
      : '';
    this.selectedFolioFilter = params.folioFilter
      ? params.folioFilter
      : '';
    this.selectedDescriptionFilter = params.descriptionFilter
      ? params.descriptionFilter
      : '';
    this.selectedProjectFilter = params.projectFilter
      ? params.projectFilter
      : '';
    if (params.usersFilter && params.usersFilter.userWhoBuys.length > 0) {
      if (!this.usersInRequisitions?.userWhoBuys) return;
      this.selectedUserWhoBuys = this.usersInRequisitions.userWhoBuys.filter((x) =>
                                  params.usersFilter!.userWhoBuys.includes(x.id));
    }
    if (params.usersFilter && params.usersFilter.userWhoRequests.length > 0) {
      if (!this.usersInRequisitions?.userWhoRequests) return;
      this.selectedUserWhoRequests = this.usersInRequisitions.userWhoRequests.filter((x) =>
                                      params.usersFilter!.userWhoRequests.includes(x.id));
    }
  }

  convertParamsToSort(params: string): void {
    if (params === undefined) return;
    const [columnName, direction] = params.split('|');
    this.selectedSortColumn = columnName;
    this.selectedSortDirection = direction;
  }

  blockEvent($event: any) {
    $event.stopPropagation();
    $event.preventDefault();
  }

  private validateFiltersEmptiness() {
    return (this.appliedFilters.applicationDate?.length === 0 &&
            this.appliedFilters.descriptionFilter === '' &&
            this.appliedFilters.projectFilter === '' &&
            this.appliedFilters.expectedDeliveryDate?.length === 0 &&
            this.appliedFilters.folioFilter === '' &&
            this.appliedFilters.idFilter === '' &&
            this.appliedFilters.priorities.length === 0 &&
            this.appliedFilters.states.length === 0 &&
            this.appliedFilters.progress.length === 0 &&
            this.appliedFilters.usersFilter?.userWhoAuthorizes.length === 0 &&
            this.appliedFilters.usersFilter?.userWhoBuys.length === 0 &&
            this.appliedFilters.usersFilter?.userWhoRequests.length === 0 &&
            this.appliedFilters.usersFilter?.userWhoReviews.length === 0
    );
  }

  private validateSelectedRequisitions() {
    this.currentPageRequisitions.forEach((y) => {
      if (this.selectedRequisitions.has(y.requisitionId)) y.isSelected = true;
    })
    if (this.currentPageRequisitions.length > 0 &&
      this.currentPageRequisitions.every((z) => z.isSelected === true)) {
      this.areAllRequisitionsSelected = true;
    } else {
      this.areAllRequisitionsSelected = false;
    }
  }

   displayAuthIdentifier(user: User) {
    if(user && user.name) 
      return user.name;

    if(user && user.key)
      return user.key;

    return 'No disponible';
  }
}
